import { ApiService } from '../api/api.service';
import { Config } from '@/environment';
import { Store } from 'vuex';
import { AxiosInstance } from 'axios';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { PaymentProviders } from '@/constants';
import { loadScript } from '@/helpers/util';
import { AnalyticsService } from '../analytics/analytics.service';
import { TransactionService } from '../transaction/transaction.service';
import { UiService } from '../ui/ui.service';
import { count } from 'console';
import { CountryCode } from '@/store/ui';
export interface PaymentService {
  loadChaseJsLibrary(): Promise<void>;
  loadPayeezyJsLibrary(): Promise<void>;
  loadUComJsLibrary(): Promise<void>;
  getPaymentProvider(countryCode: string): Promise<string>;
  isPaymentOnline(): boolean;
  isAboveStorePaymentOnline(): boolean;
  getChaseUKCallbackUrl(): string;
}

export class AppPaymentService extends ApiService implements PaymentService {
  private isChaseLibraryLoaded: boolean = false;
  private isPayeezyLibraryLoaded: boolean = false;
  private isUComLibraryLoaded: boolean = false;

  constructor(
    private config: Config,
    private ui: UiService,
    protected store: Store<AppState>,
    protected httpClient: AxiosInstance,
    protected logger: Logger,
    protected analytics: AnalyticsService,
    protected transactionService: TransactionService
  ) {
    super(store, httpClient, logger, analytics);
  }

  async getPaymentProvider(countryCode: string): Promise<string> {
    const providers = await this.transactionService.getProvidersByCountry(countryCode);
    if (providers && providers.length > 0) {
      const defaultProviders = providers.filter((provider) => provider.default === true);
      if (defaultProviders && defaultProviders.length === 1) {
        const providerId = defaultProviders[0].paymentProviderId;
        return providerId;
      }
    }

    switch (countryCode) {
      case CountryCode.CA:
        return PaymentProviders.Chase;
      case CountryCode.GB:
      case CountryCode.UK:
        return PaymentProviders.ChaseUK;
      case CountryCode.US:
        return PaymentProviders.UCom;
      default:
        return PaymentProviders.UCom;
    }
  }

  async loadChaseJsLibrary(): Promise<void> {
    if (!this.isChaseLibraryLoaded) {
      this.isChaseLibraryLoaded = true;
      return await loadScript(this.config.chase.jsLibraryUrl);
    }
  }

  async loadPayeezyJsLibrary(): Promise<void> {
    if (!this.isPayeezyLibraryLoaded) {
      this.isPayeezyLibraryLoaded = true;
      return await loadScript(this.config.payeezy.jsLibrary);
    }
  }

  async loadUComJsLibrary(): Promise<void> {
    if (!this.isUComLibraryLoaded) {
      this.isUComLibraryLoaded = true;
      return await loadScript(this.config.ucom.jsLibraryUrl);
    }
  }

  getChaseUKCallbackUrl(): string {
    return this.config.chaseuk.redirectUrl;
  }

  isAboveStorePaymentOnline(): boolean {
    const flags = this.store.state.featureFlags;
    if (flags.orderingStatus && !flags.orderingStatus.aboveStorePaymentOnline) {
      return false;
    }
    return true;
  }

  isPaymentOnline(): boolean {
    const flags = this.store.state.featureFlags;
    const isPayeezy = this.store.state.checkout.paymentProvider == PaymentProviders.Payeezy;
    const isUCom = this.store.state.checkout.paymentProvider === PaymentProviders.UCom;
    const isChase = this.store.state.checkout.paymentProvider == PaymentProviders.Chase;
    const isChaseUK = this.store.state.checkout.paymentProvider === PaymentProviders.ChaseUK;

    if (flags.orderingStatus && !flags.orderingStatus.aboveStorePaymentOnline) {
      return false;
    }

    if (flags.orderingStatus && isPayeezy && !flags.orderingStatus.isPayeezyPaymentOnline) {
      return false;
    }

    if (flags.orderingStatus && isUCom && !flags.orderingStatus.isUComPaymentOnline) {
      return false;
    }

    if (flags.orderingStatus && isChase && !flags.orderingStatus.isChasePaymentOnline) {
      return false;
    }

    if (flags.orderingStatus && isChaseUK && !flags.orderingStatus.isChaseUKPaymentOnline) {
      return false;
    }

    return true;
  }
}
